/**
 * MobileBottomNav - Bottom navigation for mobile devices
 * 
 * Features:
 * - Role-based navigation items (student, family, teacher, admin)
 * - Public navigation for unauthenticated users
 * - Safe area support for iPhone X+
 * - Hidden on desktop (md:hidden)
 * - Active state indication
 * - Haptic feedback on tap (visual)
 */

import { memo, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { 
  Home, 
  Compass, 
  Calendar, 
  User, 
  Users,
  LogIn, 
  Sparkles,
  LayoutDashboard,
  BarChart3
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { useAuthStore } from '@/stores/authStore';
import type { LucideIcon } from 'lucide-react';

interface NavItem {
  path: string;
  label: string;
  icon: LucideIcon;
  matchPaths?: string[];
}

const studentNavItems: NavItem[] = [
  { path: '/jornada-aluno', label: 'Início', icon: Home, matchPaths: ['/jornada-aluno', '/minha-jornada-aluno', '/dashboard'] },
  { path: '/descobrir-experiencias', label: 'Descobrir', icon: Compass, matchPaths: ['/descobrir-experiencias'] },
  { path: '/calendario', label: 'Calendário', icon: Calendar, matchPaths: ['/calendario'] },
  { path: '/perfil', label: 'Perfil', icon: User, matchPaths: ['/perfil'] },
];

const familyNavItems: NavItem[] = [
  { path: '/minha-jornada-familia', label: 'Início', icon: Home, matchPaths: ['/minha-jornada-familia', '/dashboard'] },
  { path: '/central-encontros', label: 'Encontros', icon: Calendar, matchPaths: ['/central-encontros', '/calendario'] },
  { path: '/descobrir-experiencias', label: 'Descobrir', icon: Compass, matchPaths: ['/descobrir-experiencias'] },
  { path: '/perfil', label: 'Perfil', icon: User, matchPaths: ['/perfil'] },
];

const teacherNavItems: NavItem[] = [
  { path: '/meu-impacto', label: 'Início', icon: Home, matchPaths: ['/meu-impacto', '/minha-jornada-educador', '/dashboard'] },
  { path: '/aulas-criadas', label: 'Experiências', icon: Sparkles, matchPaths: ['/aulas-criadas', '/minhas-turmas'] },
  { path: '/calendario', label: 'Calendário', icon: Calendar, matchPaths: ['/calendario'] },
  { path: '/perfil', label: 'Perfil', icon: User, matchPaths: ['/perfil', '/bio-educador'] },
];

const adminNavItems: NavItem[] = [
  { path: '/admin', label: 'Dashboard', icon: LayoutDashboard, matchPaths: ['/admin'] },
  { path: '/admin/analytics', label: 'Analytics', icon: BarChart3, matchPaths: ['/admin/analytics'] },
  { path: '/calendario', label: 'Calendário', icon: Calendar, matchPaths: ['/calendario'] },
  { path: '/perfil', label: 'Perfil', icon: User, matchPaths: ['/perfil'] },
];

const publicNavItems: NavItem[] = [
  { path: '/', label: 'Início', icon: Home, matchPaths: ['/', '/sobre'] },
  { path: '/descobrir-experiencias', label: 'Descobrir', icon: Compass, matchPaths: ['/descobrir-experiencias'] },
  { path: '/nossos-educadores', label: 'Educadores', icon: Users, matchPaths: ['/nossos-educadores'] },
  { path: '/autenticacao?mode=login', label: 'Entrar', icon: LogIn, matchPaths: ['/autenticacao'] },
];

function getNavItemsByRole(role: string | null, isAuthenticated: boolean): NavItem[] {
  if (!isAuthenticated) return publicNavItems;
  switch (role) {
    case 'student':
      return studentNavItems;
    case 'family':
      return familyNavItems;
    case 'teacher':
      return teacherNavItems;
    case 'admin':
      return adminNavItems;
    default:
      return studentNavItems;
  }
}

interface NavButtonProps {
  item: NavItem;
  isActive: boolean;
  onClick: () => void;
}

const NavButton = memo(({ item, isActive, onClick }: NavButtonProps) => {
  const Icon = item.icon;
  
  return (
    <button
      onClick={onClick}
      className={cn(
        "flex flex-col items-center justify-center gap-0.5 px-3 py-2 min-w-[64px] min-h-[48px]",
        "transition-all duration-200 ease-out",
        "active:scale-95 active:opacity-80",
        "focus:outline-none focus-visible:ring-2 focus-visible:ring-auli-teal focus-visible:ring-offset-2",
        isActive 
          ? "text-auli-teal" 
          : "text-muted-foreground hover:text-foreground"
      )}
      aria-current={isActive ? 'page' : undefined}
      aria-label={item.label}
    >
      <Icon 
        className={cn(
          "h-5 w-5 transition-transform duration-200",
          isActive && "scale-110"
        )} 
        strokeWidth={isActive ? 2.5 : 2}
      />
      <span className={cn(
        "text-[10px] font-medium leading-tight",
        isActive && "font-semibold"
      )}>
        {item.label}
      </span>
    </button>
  );
});

NavButton.displayName = 'NavButton';

export const MobileBottomNav = memo(() => {
  const { activeMode, user } = useAuthStore();
  const navigate = useNavigate();
  const location = useLocation();

  const isAuthenticated = !!user;
  const navItems = useMemo(() => getNavItemsByRole(activeMode, isAuthenticated), [activeMode, isAuthenticated]);

  const isPathActive = (item: NavItem): boolean => {
    const currentPath = location.pathname;
    
    // For items with query params in path (e.g. /autenticacao?mode=login)
    const itemPathBase = item.path.split('?')[0];
    if (currentPath === itemPathBase) return true;
    
    // Check matchPaths for prefix matches
    if (item.matchPaths) {
      return item.matchPaths.some(matchPath => 
        currentPath === matchPath || currentPath.startsWith(matchPath + '/')
      );
    }
    
    return false;
  };

  // Hide on auth and onboarding routes
  const hiddenRoutes = ['/login', '/signup', '/cadastro', '/auth'];
  const isHiddenRoute = hiddenRoutes.some(route => 
    location.pathname === route || location.pathname.startsWith('/auth')
  );

  if (isHiddenRoute) {
    return null;
  }

  return (
    <nav 
      className={cn(
        "fixed bottom-0 inset-x-0 z-50",
        "md:hidden", // Hidden on desktop
        "bg-background",
        "border-t border-border/50",
        "shadow-[0_-4px_16px_rgba(0,0,0,0.08)]"
      )}
      style={{
        paddingBottom: 'env(safe-area-inset-bottom, 0px)',
        backfaceVisibility: 'hidden',
        willChange: 'transform',
      }}
      role="navigation"
      aria-label="Navegação principal"
    >
      <div className="flex justify-around items-center h-14 max-w-lg mx-auto">
        {navItems.map((item) => (
          <NavButton
            key={item.path}
            item={item}
            isActive={isPathActive(item)}
            onClick={() => navigate(item.path)}
          />
        ))}
      </div>
    </nav>
  );
});

MobileBottomNav.displayName = 'MobileBottomNav';

export default MobileBottomNav;
